import { useTranslation } from "react-i18next";

interface StepSummary {
  id: number;
  icon: string;
  title: string;
  time: string;
  optional?: boolean;
}

interface Props {
  steps: StepSummary[];
  checked: Set<number>;
  progress: number;
  mandatoryDone: number;
  mandatoryTotal: number;
  mandatoryAllDone: boolean;
  expanded: number | null;
  onStepClick: (id: number) => void;
  planName: string;
}

export default function OnboardingSidebar({
  steps,
  checked,
  progress,
  mandatoryDone,
  mandatoryTotal,
  mandatoryAllDone,
  expanded,
  onStepClick,
  planName,
}: Props) {
  const { i18n } = useTranslation();
  const isEn = i18n.language === "en";

  const mandatorySteps = steps.filter((s) => !s.optional);
  const optionalSteps = steps.filter((s) => s.optional);

  const totalMinutes = steps.reduce((acc, s) => {
    const n = parseInt(s.time, 10);
    return acc + (isNaN(n) ? 0 : n);
  }, 0);

  return (
    <aside className="hidden lg:block">
      <div className="sticky top-20 space-y-4">
        {/* Plan badge */}
        <div className="flex items-center gap-2 px-3 py-2 bg-amber-50 border border-amber-100 rounded-xl">
          <i className="ri-vip-crown-line text-amber-500 text-sm" />
          <span className="text-xs font-bold text-amber-700">{planName}</span>
        </div>

        {/* Progress card */}
        <div className="bg-white border border-stone-100 rounded-2xl p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-bold text-stone-700">
              {isEn ? "Your progress" : "Tu progreso"}
            </span>
            <span className={`text-xs font-bold ${mandatoryAllDone ? "text-emerald-500" : "text-stone-400"}`}>
              {mandatoryDone}/{mandatoryTotal}
            </span>
          </div>
          <div className="w-full h-2 bg-stone-100 rounded-full overflow-hidden mb-2">
            <div
              className={`h-full rounded-full transition-all duration-500 ${mandatoryAllDone ? "bg-emerald-400" : "bg-amber-400"}`}
              style={{ width: `${progress}%` }}
            />
          </div>
          <div className="flex items-center justify-between">
            <span className="text-xs text-stone-400">
              {isEn ? `~${totalMinutes} min total` : `~${totalMinutes} min en total`}
            </span>
            {mandatoryAllDone && (
              <span className="flex items-center gap-1 text-xs font-bold text-emerald-500">
                <i className="ri-check-double-line text-xs" />
                {isEn ? "Done!" : "¡Listo!"}
              </span>
            )}
          </div>
        </div>

        {/* Mandatory steps list */}
        <div className="bg-white border border-stone-100 rounded-2xl overflow-hidden">
          <div className="px-4 py-3 border-b border-stone-50">
            <p className="text-xs font-bold text-stone-400 uppercase tracking-widest">
              {isEn ? "Required steps" : "Pasos obligatorios"}
            </p>
          </div>
          <div className="divide-y divide-stone-50">
            {mandatorySteps.map((step) => {
              const isDone = checked.has(step.id);
              const isActive = expanded === step.id;
              return (
                <button
                  key={step.id}
                  onClick={() => onStepClick(step.id)}
                  className={`w-full flex items-center gap-3 px-4 py-3 text-left transition-all cursor-pointer group ${
                    isActive
                      ? "bg-amber-50"
                      : isDone
                      ? "hover:bg-stone-50"
                      : "hover:bg-stone-50"
                  }`}
                >
                  {/* Status circle */}
                  <div className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 border-2 transition-all ${
                    isDone
                      ? "bg-emerald-400 border-emerald-400"
                      : isActive
                      ? "border-amber-400 bg-amber-50"
                      : "border-stone-300 group-hover:border-amber-300"
                  }`}>
                    {isDone ? (
                      <i className="ri-check-line text-white text-xs" />
                    ) : isActive ? (
                      <div className="w-2 h-2 rounded-full bg-amber-400" />
                    ) : null}
                  </div>

                  {/* Icon + title */}
                  <div className="flex-1 min-w-0">
                    <p className={`text-xs font-semibold leading-snug truncate ${
                      isDone ? "text-stone-400 line-through" : isActive ? "text-amber-700" : "text-stone-700"
                    }`}>
                      {step.title}
                    </p>
                    <p className="text-xs text-stone-400">~{step.time}</p>
                  </div>

                  <i className={`ri-arrow-right-s-line text-sm flex-shrink-0 transition-transform ${
                    isActive ? "text-amber-500 rotate-90" : "text-stone-300 group-hover:text-stone-500"
                  }`} />
                </button>
              );
            })}
          </div>
        </div>

        {/* Optional steps */}
        {optionalSteps.length > 0 && (
          <div className="bg-white border border-stone-100 rounded-2xl overflow-hidden">
            <div className="px-4 py-3 border-b border-stone-50">
              <p className="text-xs font-bold text-stone-400 uppercase tracking-widest">
                {isEn ? "Optional" : "Opcional"}
              </p>
            </div>
            <div className="divide-y divide-stone-50">
              {optionalSteps.map((step) => {
                const isDone = checked.has(step.id);
                const isActive = expanded === step.id;
                return (
                  <button
                    key={step.id}
                    onClick={() => onStepClick(step.id)}
                    className={`w-full flex items-center gap-3 px-4 py-3 text-left transition-all cursor-pointer group ${
                      isActive ? "bg-amber-50" : "hover:bg-stone-50"
                    }`}
                  >
                    <div className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 border-2 transition-all ${
                      isDone ? "bg-emerald-400 border-emerald-400" : "border-stone-200 border-dashed group-hover:border-amber-300"
                    }`}>
                      {isDone && <i className="ri-check-line text-white text-xs" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className={`text-xs font-semibold leading-snug truncate ${
                        isDone ? "text-stone-400 line-through" : "text-stone-500"
                      }`}>
                        {step.title}
                      </p>
                      <p className="text-xs text-stone-400">~{step.time}</p>
                    </div>
                    <i className={`ri-arrow-right-s-line text-sm flex-shrink-0 transition-transform ${
                      isActive ? "text-amber-500 rotate-90" : "text-stone-300 group-hover:text-stone-500"
                    }`} />
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Help */}
        <div className="bg-stone-900 rounded-2xl p-4">
          <div className="flex items-center gap-2 mb-2">
            <i className="ri-customer-service-2-line text-amber-400 text-sm" />
            <p className="text-xs font-bold text-white">
              {isEn ? "Need help?" : "¿Necesitas ayuda?"}
            </p>
          </div>
          <p className="text-xs text-stone-400 mb-3 leading-relaxed">
            {isEn
              ? "Our team responds in under 2 hours."
              : "Nuestro equipo responde en menos de 2 horas."}
          </p>
          <a
            href="mailto:soporteauria@gmail.com"
            className="text-xs font-semibold text-amber-400 hover:text-amber-300 transition-colors cursor-pointer flex items-center gap-1.5"
          >
            <i className="ri-mail-line text-xs" />
            soporteauria@gmail.com
          </a>
        </div>
      </div>
    </aside>
  );
}
